import React, { useMemo, useState } from "react";
import styled from "styled-components";
import { Alert, Button, Card, InputGroup } from "@blueprintjs/core";
import { faJira, faSlack } from "@fortawesome/free-brands-svg-icons";
import { faQuestionCircle } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Tooltip } from "@mantine/core";

import { Integration, IntegrationType } from "@pages/settings/types";

const CONFIG = {
  [IntegrationType.Jira]: {
    name: "Jira",
    icon: faJira,
  },
  [IntegrationType.SlackApp]: {
    name: "Slack",
    icon: faSlack,
  },
};

// placeholder for token, since it is never loaded from the backend
const TOKEN_PLACEHOLDER = "tokenplaceholder";

const IntegrationCard = styled(Card)`
  padding: 1rem;
  &:not(:first-child) {
    margin-top: 1.2rem;
  }
`;

const Label = styled.span`
  min-width: 6em;
`;

const InputLabel = styled.span`
  color: #777;
  min-width: 6em;
`;

const WideInput = styled(InputGroup)`
  width: 100%;
  flex-grow: 1;
  display: flex;
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
`;

const Header = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: none;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.7em;
  & > :not(:first-child) {
    margin-left: 1em;
  }
  & {
    white-space: nowrap;
  }
`;

const HeaderLeft = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: none;
  flex-grow: 1;
  align-items: center;
  & > :not(:first-child) {
    margin-left: 1em;
  }
`;

type Props = {
  save: (
    integration: Integration,
    index: number,
    cb: (integration: Integration) => void,
    token?: string
  ) => void;
  onDelete: (i: Integration) => void;
  inheritedIntegration: Integration;
  index: number;
};

export const IntegrationPanelCard: React.FC<Props> = ({
  save,
  onDelete,
  inheritedIntegration,
  index,
}) => {
  const [integration, setIntegration] =
    useState<Integration>(inheritedIntegration);
  const [integrationToken, setIntegrationToken] = useState<string | undefined>(
    inheritedIntegration.id ? TOKEN_PLACEHOLDER : undefined
  );
  const [showDeleteWarning, setShowDeleteWarning] = useState<boolean>(false);

  const saveIntegration = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    save(integration, index, () => {}, integrationToken);
  };

  const deleteIntegration = useMemo(
    () => (event: any) => {
      event.preventDefault();
      onDelete(integration);
    },
    [integration, onDelete]
  );

  const integrationConfig = CONFIG[integration.type];

  const integrationHasUnsavedChanges = useMemo(
    () =>
      integration !== inheritedIntegration ||
      integrationToken !== TOKEN_PLACEHOLDER,
    [integration, inheritedIntegration, integrationToken]
  );

  const card = (
    <Content>
      <Header>
        <HeaderLeft>
          <Label>
            {"icon" in integrationConfig ? (
              <>
                <FontAwesomeIcon icon={integrationConfig.icon} /> &nbsp;
              </>
            ) : null}
            {integrationConfig.name}
          </Label>
        </HeaderLeft>
        <Button
          intent="danger"
          minimal
          small
          onClick={() => setShowDeleteWarning(true)}
        >
          Remove integration
        </Button>
      </Header>
      <form onSubmit={saveIntegration}>
        <Header>
          <InputLabel>Name</InputLabel>
          <WideInput
            placeholder="Integration name..."
            value={integration.name}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setIntegration({ ...integration, name: e.target.value });
            }}
          />
        </Header>
        <Header>
          <InputLabel>Email</InputLabel>
          <WideInput
            placeholder="Atlassian account email address..."
            value={integration.email}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setIntegration({ ...integration, email: e.target.value });
            }}
            className="fs-mask dd-privacy-mask"
          />
        </Header>
        <Header>
          <InputLabel>URL</InputLabel>
          <WideInput
            placeholder="https://your-domain.atlassian.net"
            value={integration.url}
            type={"url"}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setIntegration({ ...integration, url: e.target.value });
            }}
            className="fs-mask dd-privacy-mask"
          />
        </Header>
        <Header>
          <InputLabel>
            Project key{" "}
            <Tooltip
              withArrow
              withinPortal
              position="right"
              label={
                <>
                  The prefix for tasks created within a project (ex: "Text
                  Project" might have the prefix "TP").
                  <br />
                  This is the project where this integration will create issues.
                </>
              }
            >
              <FontAwesomeIcon icon={faQuestionCircle} />
            </Tooltip>
          </InputLabel>
          <WideInput
            placeholder={"Project key..."}
            value={integration.meta.project_key ?? ""}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setIntegration({
                ...integration,
                meta: {
                  issue_name: integration.meta.issue_name,
                  project_key: e.target.value,
                },
              });
            }}
            fill={false}
            className="fs-mask dd-privacy-mask"
          />
        </Header>
        <Header>
          <InputLabel>
            Issue type{" "}
            <Tooltip
              withArrow
              withinPortal
              position="right"
              label={"The type of issue for Semgrep findings (ex: Bug)."}
            >
              <FontAwesomeIcon icon={faQuestionCircle} />
            </Tooltip>
          </InputLabel>
          <WideInput
            placeholder={"Issue type..."}
            value={integration.meta.issue_name ?? ""}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setIntegration({
                ...integration,
                meta: {
                  issue_name: e.target.value,
                  project_key: integration.meta.project_key,
                },
              });
            }}
            fill={false}
            className="fs-mask dd-privacy-mask"
          />
        </Header>
        <Header>
          <HeaderLeft>
            <InputLabel>
              API token{" "}
              <Tooltip
                withArrow
                withinPortal
                position="right"
                label={<>Atlassian account API token.</>}
              >
                <FontAwesomeIcon icon={faQuestionCircle} />
              </Tooltip>
            </InputLabel>
            <WideInput
              placeholder={"API token..."}
              type={"password"}
              value={integrationToken}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setIntegrationToken(e.target.value);
              }}
              fill={false}
              className="fs-mask dd-privacy-mask"
            />
          </HeaderLeft>
          <Button
            type="submit"
            intent="primary"
            disabled={!integrationHasUnsavedChanges}
          >
            Save
          </Button>
        </Header>
      </form>
    </Content>
  );

  return (
    <>
      <Alert
        className="deleteIntegrationWarning"
        cancelButtonText="Cancel"
        confirmButtonText="Remove"
        icon="trash"
        style={{ width: "400px" }}
        intent="danger"
        isOpen={showDeleteWarning}
        onCancel={() => setShowDeleteWarning(false)}
        onConfirm={deleteIntegration}
      >
        <p>
          Are you sure you want to remove{" "}
          {!integration.name ? "this integration" : <b>{integration.name}</b>}?
        </p>
        <p>
          Choosing "Remove" will remove this integration from all policies that
          currently reference it.
        </p>
      </Alert>
      <IntegrationCard className="notification-option">{card}</IntegrationCard>
    </>
  );
};
